import type React from 'react';

const gridColumns = {
  mobile: 8,
  tablet: 12,
  desktop: 16,
} as const;

const gridRows = {
  mobile: 6,
  tablet: 7,
  desktop: 8,
} as const;

type GridBackgroundProps = {
  children?: React.ReactNode;
  darkBg?: boolean;
};

export function GridBackground({
  children,
  darkBg = false,
}: GridBackgroundProps) {
  const bgClass = darkBg
    ? 'bg-gray-100 dark:bg-gray-900'
    : 'bg-white dark:bg-gray-950';

  const fadeClass = darkBg
    ? 'from-gray-100 dark:from-gray-900'
    : 'from-white dark:from-gray-950';

  return (
    <div className={`relative ${bgClass}`}>
      <div className='pointer-events-none absolute inset-0 z-0 max-h-screen overflow-hidden'>
        <div className='absolute inset-0 z-0 grid size-full grid-cols-[repeat(8,1fr)] sm:grid-cols-[repeat(12,1fr)] md:grid-cols-[repeat(16,1fr)]'>
          {Array.from({ length: gridColumns.desktop }, (_, index) => (
            <div
              className={[
                'relative h-full overflow-hidden border-black/5 border-r last:border-r-0 dark:border-white/5',
                index >= gridColumns.mobile ? 'hidden sm:block' : '',
                index >= gridColumns.tablet ? 'sm:hidden md:block' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              key={`col-${String(index)}`}
            >
              {index % 2 === 1 && (
                <div
                  className='absolute -top-full h-24 w-px sm:h-28 md:h-32'
                  style={{
                    animation: 'slide-down 5s linear infinite',
                    animationDelay: `${String(index * 0.3)}s`,
                    background: 'var(--gradient-vertical)',
                  }}
                />
              )}
            </div>
          ))}
        </div>

        <div className='absolute inset-0 z-0 grid size-full grid-rows-[repeat(6,1fr)] sm:grid-rows-[repeat(7,1fr)] md:grid-rows-[repeat(8,1fr)]'>
          {Array.from({ length: gridRows.desktop }, (_, index) => (
            <div
              className={[
                'relative w-full overflow-hidden border-black/5 border-b last:border-b-0 dark:border-white/5',
                index >= gridRows.mobile ? 'hidden sm:block' : '',
                index >= gridRows.tablet ? 'sm:hidden md:block' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              key={`row-${String(index)}`}
            >
              {index % 2 === 1 && (
                <div
                  className='absolute -left-full h-px w-24 sm:w-28 md:w-32'
                  style={{
                    animation: 'slide-right 5s linear infinite',
                    animationDelay: `${String(index * 0.3)}s`,
                    background: 'var(--gradient-horizontal)',
                  }}
                />
              )}
            </div>
          ))}
        </div>

        <div
          className={`absolute inset-x-0 bottom-0 h-24 bg-linear-to-t ${fadeClass} to-transparent sm:h-28 md:h-32`}
        />
        <style>{`
          :root {
            --gradient-vertical: linear-gradient(0deg, transparent 0%, #FF69B4 40%, #4169E1 60%, transparent 100%);
            --gradient-horizontal: linear-gradient(90deg, transparent 0%, #FF69B4 40%, #4169E1 60%, transparent 100%);
          }

          .dark {
            --gradient-vertical: linear-gradient(0deg, transparent 0%, #FF1493 40%, #1E90FF 60%, transparent 100%);
            --gradient-horizontal: linear-gradient(90deg, transparent 0%, #FF1493 40%, #1E90FF 60%, transparent 100%);
          }

          @media (prefers-reduced-motion) {
            [style*='animation:'] {
              animation: none !important;
            }
          }
  
          @keyframes slide-down {
            0% {
              top: -10%;
            }
            100% {
              top: 100%;
            }
          }
  
          @keyframes slide-right {
            0% {
              left: -10%;
            }
            100% {
              left: 100%;
            }
          }
        `}</style>
      </div>
      <div className='relative z-10'>{children}</div>
    </div>
  );
}
